import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  Tooltip,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SettingsIcon from "@mui/icons-material/Settings";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import PercentIcon from "@mui/icons-material/Percent";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import HistoryIcon from "@mui/icons-material/History";
import useAlert from "../../../hooks/useAlert";
import usePedido from "../../../hooks/usePedido";
import useConfigCarrinho from "../../../hooks/useConfigCarrinho";
import {
  getQuote,
  executeQuote,
  createExecuteTransactionRequest,
  checkCryptoAccount,
  createCryptoSubaccount,
  getBalance,
  getMoedasDisponiveis, // Adicionada nova função
} from "../../../api/Carrinho/Cripto";
import { useAuth } from "../../../hooks/AuthContext";
import TransacoesCripto from "./TransacoesCripto";

// Estilo Binance Light Theme (inspirado na Binance mas com cores claras)
const binanceLightTheme = {
  bgPrimary: "#ffffff",
  bgSecondary: "#f8f9fa",
  bgTertiary: "#f1f2f6",
  textPrimary: "#1e2329",
  textSecondary: "#707a8a",
  success: "#0ecb81",
  warning: "#f6465d",
  primary: "#f0b90b",
  border: "#e6e8ea",
  hover: "#f5f5f5",
  successLight: "#e8f7f1",
  warningLight: "#ffeef0",
  primaryLight: "#fef7e0",
  sellColor: "#f0b90b", // Amarelo para venda
  sellLight: "#fef7e0", // Amarelo claro para venda
};

// Ícones para moedas (mantidos fixos)
const moedaIcons = {
  BRL: <AttachMoneyIcon />,
  USDT: <CurrencyExchangeIcon />,
  USDC: <AccountBalanceIcon />,
};

// Componente SaldoCripto - Estilo Binance Light
const SaldoCripto = ({
  refreshTrigger,
  showDetailed = true,
  hidden = false,
  onToggleVisibility,
  isVisible,
  moedasCripto = [], // Nova prop para moedas da API
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saldo, setSaldo] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const formatCurrency = (value, currency) => {
    if (!value && value !== 0) return "0";
    const num = parseFloat(value);

    if (currency === "BRL") {
      return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return num.toLocaleString("pt-BR", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 8,
    });
  };

  const fetchSaldo = async () => {
    if (!user?.id) {
      setSaldo(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getBalance(user.id);

      // Ajuste aqui: verifica se response.data existe e tem os campos necessários
      if (response.data && (response.success || response.data.success)) {
        // Garante que os saldos existam mesmo quando vazios
        const saldoCompleto = {
          BRL: response.data.BRL || 0,
          USDT: response.data.USDT || 0,
          USDC: response.data.USDC || 0,
          BRL_locked: response.data.BRL_locked || 0,
          USDT_locked: response.data.USDT_locked || 0,
          USDC_locked: response.data.USDC_locked || 0,
          success: response.data.success || response.success || false,
          status: response.status,
        };

        setSaldo(saldoCompleto);
        setLastUpdate(new Date().toLocaleTimeString("pt-BR"));

        if (response.errorMessage) {
          console.warn("Aviso ao buscar saldo:", response.errorMessage);
        }
      } else {
        // Se não tem dados, verifica se é 204
        if (response.status === 204) {
          const saldoVazio = {
            BRL: 0,
            USDT: 0,
            USDC: 0,
            BRL_locked: 0,
            USDT_locked: 0,
            USDC_locked: 0,
            success: true,
            status: 204,
          };
          setSaldo(saldoVazio);
          setLastUpdate(new Date().toLocaleTimeString("pt-BR"));
        } else {
          throw new Error(response.errorMessage || "Erro ao buscar saldo");
        }
      }
    } catch (error) {
      console.error("Erro ao buscar saldo:", error);
      setError(error.message || "Não foi possível carregar os saldos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSaldo();
  }, [user, refreshTrigger]);

  const handleRefresh = () => {
    fetchSaldo();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={20} sx={{ color: binanceLightTheme.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
        {error}
      </Alert>
    );
  }

  if (!saldo) {
    return null;
  }

  // Se estiver oculto
  if (hidden) {
    return (
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: binanceLightTheme.bgSecondary,
          borderRadius: 2,
          border: `1px solid ${binanceLightTheme.border}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceWalletIcon
              sx={{ color: binanceLightTheme.textSecondary }}
            />
            <Typography
              variant="body2"
              sx={{ color: binanceLightTheme.textSecondary }}
            >
              Saldos Ocultos
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onToggleVisibility}
            sx={{ color: binanceLightTheme.textSecondary }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // Moedas para exibir (BRL fixo + moedas da API que tenham ícone definido)
  const moedasParaExibir = [
    {
      key: "BRL",
      label: "Real",
      icon: moedaIcons.BRL,
      type: "FIAT",
      color: "#10b981",
    },
    ...moedasCripto
      .filter((moeda) => moedaIcons[moeda.codigo]) // Apenas moedas com ícone definido
      .map((moeda) => ({
        key: moeda.codigo,
        label: moeda.nome,
        icon: moedaIcons[moeda.codigo],
        type: "CRYPTO",
        color: moeda.codigo === "USDT" ? "#3b82f6" : "#8b5cf6",
      })),
  ];

  // Calcular total em cripto dinamicamente
  const totalCripto = moedasCripto.reduce((total, moeda) => {
    return total + (saldo[moeda.codigo] || 0);
  }, 0);

  // Versão detalhada estilo Binance Light
  return (
    <Box sx={{ mb: 3 }}>
      {/* Cabeçalho */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          p: 2,
          bgcolor: binanceLightTheme.bgSecondary,
          borderRadius: 2,
          border: `1px solid ${binanceLightTheme.border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AccountBalanceWalletIcon sx={{ color: binanceLightTheme.primary }} />
          <Box>
            <Typography
              variant="h6"
              sx={{ color: binanceLightTheme.textPrimary, fontWeight: 600 }}
            >
              Saldo
            </Typography>
            {lastUpdate && (
              <Typography
                variant="caption"
                sx={{ color: binanceLightTheme.textSecondary }}
              >
                Atualizado às {lastUpdate}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {onToggleVisibility && (
            <Tooltip title="Ocultar saldos">
              <IconButton
                size="small"
                onClick={onToggleVisibility}
                sx={{ color: binanceLightTheme.textSecondary }}
              >
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={handleRefresh}
            sx={{ color: binanceLightTheme.textSecondary }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Grid de saldos */}
      <Grid container spacing={1.5}>
        {moedasParaExibir.map((currency) => (
          <Grid item xs={12} sm={4} key={currency.key}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: binanceLightTheme.bgPrimary,
                border: `1px solid ${binanceLightTheme.border}`,
                borderRadius: 2,
                height: "100%",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                  borderColor: currency.color,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    bgcolor: `${currency.color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: currency.color,
                  }}
                >
                  {currency.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: binanceLightTheme.textPrimary,
                      fontWeight: 600,
                    }}
                  >
                    {currency.label}
                  </Typography>
                  <Chip
                    label={currency.type}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      bgcolor: `${currency.color}15`,
                      color: currency.color,
                      border: "none",
                      fontWeight: 500,
                    }}
                  />
                </Box>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  color: binanceLightTheme.textPrimary,
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                {formatCurrency(saldo[currency.key], currency.key)}
                {currency.key !== "BRL" && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: binanceLightTheme.textSecondary, ml: 0.5 }}
                  >
                    {currency.key}
                  </Typography>
                )}
              </Typography>

              {saldo[`${currency.key}_locked`] > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: `1px solid ${binanceLightTheme.border}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: binanceLightTheme.textSecondary,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <LockIcon fontSize="small" />
                    Bloqueado
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: binanceLightTheme.textPrimary,
                      fontWeight: 500,
                    }}
                  >
                    {formatCurrency(
                      saldo[`${currency.key}_locked`],
                      currency.key,
                    )}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Resumo total - Corrigido para tamanhos iguais */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          bgcolor: binanceLightTheme.bgPrimary,
          border: `1px solid ${binanceLightTheme.border}`,
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        <Grid container>
          {/* Total em Cripto - Tamanho igual */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                height: "100%",
                borderRight: { md: `1px solid ${binanceLightTheme.border}` },
                borderBottom: {
                  xs: `1px solid ${binanceLightTheme.border}`,
                  md: "none",
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: binanceLightTheme.textSecondary,
                  mb: 1,
                  display: "block",
                  fontWeight: 500,
                }}
              >
                Total em Cripto
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: binanceLightTheme.textPrimary,
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                {formatCurrency(totalCripto, "USDT")}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {moedasCripto.map((moeda) => (
                  <Box key={moeda.codigo}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: binanceLightTheme.textSecondary,
                        fontWeight: 500,
                        display: "block",
                      }}
                    >
                      {moeda.codigo}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: binanceLightTheme.textPrimary,
                        fontWeight: 600,
                      }}
                    >
                      {formatCurrency(saldo[moeda.codigo] || 0, moeda.codigo)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Valor Total Geral - Tamanho igual */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: binanceLightTheme.textSecondary,
                    fontWeight: 500,
                  }}
                >
                  Valor Total Geral
                </Typography>
                <Chip
                  label="ESTIMADO"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    bgcolor: `${binanceLightTheme.primary}20`,
                    color: binanceLightTheme.primary,
                    border: "none",
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: binanceLightTheme.textPrimary,
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                {formatCurrency(saldo.BRL + totalCripto * 1, "BRL")}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: binanceLightTheme.textSecondary,
                      fontWeight: 500,
                      display: "block",
                    }}
                  >
                    FIAT (BRL)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: binanceLightTheme.textPrimary,
                      fontWeight: 600,
                    }}
                  >
                    {formatCurrency(saldo.BRL, "BRL")}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: binanceLightTheme.textSecondary,
                      fontWeight: 500,
                      display: "block",
                    }}
                  >
                    Cripto em BRL
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: binanceLightTheme.textPrimary,
                      fontWeight: 600,
                    }}
                  >
                    {formatCurrency(totalCripto * 1, "BRL")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const Cripto = ({ idTipoOperacao }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showAlertError, showAlertSuccess } = useAlert();
  const { criarPedido, loading: loadingPedido } = usePedido();
  const { criarConfigCarrinho } = useConfigCarrinho();

  const [tipoOperacao, setTipoOperacao] = useState("BUY");
  const [moedaCripto, setMoedaCripto] = useState("USDT");
  const [quantidade, setQuantidade] = useState("");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [loadingExecute, setLoadingExecute] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);

  const [hasCryptoAccount, setHasCryptoAccount] = useState(null);
  const [checkingAccount, setCheckingAccount] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [showCreateAccountDialog, setShowCreateAccountDialog] = useState(false);
  const [hasShownAccountAlert, setHasShownAccountAlert] = useState(false);

  const [refreshSaldo, setRefreshSaldo] = useState(0);
  const [showSaldo, setShowSaldo] = useState(false);
  const [percentage, setPercentage] = useState(0);

  // Estado para controlar se está aguardando nova cotação
  const [awaitingNewQuote, setAwaitingNewQuote] = useState(false);
  // Estado para controlar o saldo do usuário
  const [userBalance, setUserBalance] = useState({
    BRL: 0,
    USDT: 0,
    USDC: 0,
    BRL_locked: 0,
    USDT_locked: 0,
    USDC_locked: 0,
  });

  // Estados para moedas da API
  const [moedasCripto, setMoedasCripto] = useState([]);
  const [loadingMoedas, setLoadingMoedas] = useState(true);
  const [errorMoedas, setErrorMoedas] = useState(null);

  const expirationTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const moedaIds = {
    BRL: 1,
    USDT: 2,
    USDC: 3,
  };

  const porcentagens = [25, 50, 75, 100];

  // Função para buscar moedas da API
  const fetchMoedasDisponiveis = async () => {
    try {
      setLoadingMoedas(true);
      setErrorMoedas(null);

      const response = await getMoedasDisponiveis();

      if (response.success && Array.isArray(response.data)) {
        // Filtrar apenas criptomoedas ativas (excluindo BRL)
        const criptomoedas = response.data.filter(
          (moeda) => moeda.simbolo !== "BRL" && moeda.ativo === true,
        );

        // Mapear para o formato esperado pelo componente
        const moedasFormatadas = criptomoedas.map((moeda) => {
          let icon;
          // Mantém ícones fixos para moedas conhecidas
          switch (moeda.simbolo) {
            case "USDT":
              icon = <CurrencyExchangeIcon />;
              break;
            case "USDC":
              icon = <AccountBalanceIcon />;
              break;
            default:
              icon = <CurrencyExchangeIcon />;
          }

          return {
            codigo: moeda.simbolo,
            nome: moeda.descricao,
            icon: icon,
            id: moeda.id,
            numeroCasaDecimais: moeda.numeroCasaDecimais || 8,
            tipo: moeda.tipo?.descricao || "Criptomoeda",
            ativo: moeda.ativo,
          };
        });

        setMoedasCripto(moedasFormatadas);

        // Se não houver moeda selecionada e tivermos moedas, seleciona a primeira
        if (!moedaCripto && moedasFormatadas.length > 0) {
          setMoedaCripto(moedasFormatadas[0].codigo);
        }
      } else {
        console.error("Erro ao buscar moedas:", response.errorMessage);
        setErrorMoedas(response.errorMessage || "Erro ao carregar moedas");
        // Fallback para moedas padrão se a API falhar
        const moedasPadrao = [
          {
            codigo: "USDT",
            nome: "Tether",
            icon: <CurrencyExchangeIcon />,
            id: 75,
            numeroCasaDecimais: 8,
            ativo: true,
          },
          {
            codigo: "USDC",
            nome: "USD Coin",
            icon: <AccountBalanceIcon />,
            id: 76,
            numeroCasaDecimais: 8,
            ativo: true,
          },
        ];
        setMoedasCripto(moedasPadrao);
      }
    } catch (error) {
      console.error("Erro ao buscar moedas disponíveis:", error);
      setErrorMoedas("Erro de conexão ao buscar moedas");
      // Fallback para moedas padrão
      const moedasPadrao = [
        {
          codigo: "USDT",
          nome: "Tether",
          icon: <CurrencyExchangeIcon />,
          id: 75,
          numeroCasaDecimais: 8,
          ativo: true,
        },
        {
          codigo: "USDC",
          nome: "USD Coin",
          icon: <AccountBalanceIcon />,
          id: 76,
          numeroCasaDecimais: 8,
          ativo: true,
        },
      ];
      setMoedasCripto(moedasPadrao);
    } finally {
      setLoadingMoedas(false);
    }
  };

  // Função para buscar o saldo do usuário
  const fetchUserBalance = async () => {
    if (!user?.id) return;

    try {
      const response = await getBalance(user.id);
      if (response.data && response.data.success) {
        setUserBalance(response.data);
      }
    } catch (error) {
      console.error("Erro ao buscar saldo do usuário:", error);
    }
  };

  const atualizarSaldo = () => {
    setRefreshSaldo((prev) => prev + 1);
    fetchUserBalance();
  };

  const getMoedaOrigem = () => (tipoOperacao === "BUY" ? "BRL" : moedaCripto);
  const getMoedaDestino = () => (tipoOperacao === "BUY" ? moedaCripto : "BRL");

  const formatNumber = (value, decimals = 8, currency = "") => {
    if (!value && value !== 0) return "0";
    const num = parseFloat(value);

    if (currency === "BRL" || decimals === 2) {
      return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    const formatted = num.toLocaleString("pt-BR", {
      minimumFractionDigits: Math.min(decimals, 8),
      maximumFractionDigits: Math.min(decimals, 8),
    });

    return currency ? `${formatted} ${currency}` : formatted;
  };

  const calcularTempoRestante = (expirationDate) => {
    const diff = new Date(expirationDate).getTime() - new Date().getTime();
    return Math.max(0, Math.floor(diff / 1000));
  };

  const formatarTempo = (segundos) => {
    if (segundos <= 0) return "00:00";
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const verificarContaCripto = useCallback(async () => {
    if (!user?.id) {
      setHasCryptoAccount(null);
      return false;
    }

    setCheckingAccount(true);
    try {
      const result = await checkCryptoAccount(user.id);
      const hasAccount = result.hasAccount || false;
      setHasCryptoAccount(hasAccount);

      if (!hasAccount && !hasShownAccountAlert) {
        setHasShownAccountAlert(true);
      }

      return hasAccount;
    } catch (error) {
      console.error("Erro ao verificar conta cripto:", error);
      setHasCryptoAccount(false);
      return false;
    } finally {
      setCheckingAccount(false);
    }
  }, [user, hasShownAccountAlert]);

  const criarContaCripto = async () => {
    if (!user?.id) return;

    setCreatingAccount(true);
    setShowCreateAccountDialog(false);

    try {
      const result = await createCryptoSubaccount(user.id);

      if (result.success) {
        showAlertSuccess("Conta de criptomoedas criada com sucesso!");
        setHasCryptoAccount(true);
        setHasShownAccountAlert(false);
        atualizarSaldo();

        setTimeout(() => {
          buscarCotacao();
        }, 1000);
      } else {
        throw new Error(result.errorMessage || "Erro ao criar conta");
      }
    } catch (error) {
      console.error("Erro ao criar conta cripto:", error);
      showAlertError(error.message || "Erro ao criar conta de criptomoedas");
    } finally {
      setCreatingAccount(false);
    }
  };

  const podeBuscarCotacao = useCallback(() => {
    return user?.id && hasCryptoAccount === true;
  }, [user, hasCryptoAccount]);

  // Handler para mudança de quantidade com debounce
  const handleQuantidadeChange = (e) => {
    const rawValue = e.target.value;

    // Normaliza substituindo vírgula por ponto
    const normalizedValue = rawValue.replace(",", ".");
    // Atualiza o estado com o valor normalizado
    setQuantidade(normalizedValue);
    setPercentage(0); // Reseta a porcentagem quando digita manualmente

    // Busca cotação automaticamente quando a quantidade muda
    // Usa normalizedValue em vez de newValue/rawValue
    if (
      normalizedValue &&
      parseFloat(normalizedValue) > 0 &&
      podeBuscarCotacao()
    ) {
      // Debounce para evitar chamadas excessivas
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      setAwaitingNewQuote(true);

      debounceTimeoutRef.current = setTimeout(() => {
        buscarCotacao();
      }, 500);
    }
  };

  // Função para calcular a quantidade baseada na porcentagem do saldo
  const calcularQuantidadePorPorcentagem = (pct) => {
    if (!quote?.price) return "0";

    const moedaSelecionada = moedasCripto.find((m) => m.codigo === moedaCripto);
    const casasDecimais = moedaSelecionada?.numeroCasaDecimais || 8;

    const saldoDisponivel =
      tipoOperacao === "BUY"
        ? userBalance.BRL // Para compra, usa saldo em BRL
        : userBalance[moedaCripto]; // Para venda, usa saldo da criptomoeda

    if (saldoDisponivel <= 0) return "0";

    const valorDisponivel =
      tipoOperacao === "BUY"
        ? saldoDisponivel // BRL disponível para compra
        : saldoDisponivel * quote.price; // Valor em BRL da cripto disponível para venda

    const valorPorcentagem = (valorDisponivel * pct) / 100;

    // Para compra, quantidade = valor em BRL / preço
    // Para venda, quantidade = valor em BRL da cripto / preço
    if (tipoOperacao === "BUY") {
      return (valorPorcentagem / quote.price).toFixed(casasDecimais);
    } else {
      return ((saldoDisponivel * pct) / 100).toFixed(casasDecimais);
    }
  };

  const handlePercentageClick = (pct) => {
    setPercentage(pct);
    const novaQuantidade = calcularQuantidadePorPorcentagem(pct);
    setQuantidade(novaQuantidade);

    // Busca cotação automaticamente quando usa porcentagem
    if (
      novaQuantidade &&
      parseFloat(novaQuantidade) > 0 &&
      podeBuscarCotacao()
    ) {
      setAwaitingNewQuote(true);
      buscarCotacao();
    }
  };

  const buscarCotacao = useCallback(
    async (isInitial = false) => {
      if (!podeBuscarCotacao()) {
        if (isInitial) setLoading(false);
        return;
      }

      // Marca que está aguardando nova cotação
      if (!isInitial) {
        setAwaitingNewQuote(true);
        setLoadingQuote(true);
      }

      try {
        // Usa a quantidade informada ou um valor padrão
        let valorParaCotacao;
        if (quantidade && parseFloat(quantidade) > 0) {
          valorParaCotacao = parseFloat(quantidade);
        } else {
          // Valor padrão baseado no tipo de operação e saldo disponível
          if (tipoOperacao === "BUY") {
            valorParaCotacao = 100; // Valor padrão para compra
          } else {
            // Para venda, usa o saldo disponível da moeda ou 10 como padrão
            const saldoMoeda = userBalance[moedaCripto] || 0;
            valorParaCotacao =
              saldoMoeda > 10 ? 10 : Math.max(saldoMoeda * 0.1, 1);
          }
        }

        const quoteRequest = {
          idOrigem: user.id,
          moedaOrigem: getMoedaOrigem(),
          moedaDestino: getMoedaDestino(),
          market: `${moedaCripto}-BRL`,
          valor: valorParaCotacao,
          customer_side: tipoOperacao,
          tipoOperacao: tipoOperacao,
        };

        const response = await getQuote(user.id, quoteRequest);

        if (response && response.errorMessage) {
          throw new Error(response.errorMessage);
        }

        if (response && response.data && response.data.success) {
          const quoteData = {
            ...response.data,
            moedaOrigem: getMoedaOrigem(),
            moedaDestino: getMoedaDestino(),
            expiration: new Date(response.data.expiration),
            timestamp: new Date(response.data.timestamp),
            quantidadeUsada: valorParaCotacao,
          };

          setQuote(quoteData);
          setAwaitingNewQuote(false); // IMPORTANTE: Resetar o flag
        } else {
          throw new Error(response?.errorMessage || "Erro ao buscar cotação");
        }
      } catch (error) {
        console.error("Erro:", error);
        if (!isInitial) {
          showAlertError(
            error.message || "Erro ao buscar cotação. Tente novamente.",
          );
        }
        setAwaitingNewQuote(false); // IMPORTANTE: Resetar mesmo em caso de erro
      } finally {
        if (isInitial) {
          setLoading(false);
        }
        setLoadingQuote(false);
      }
    },
    [
      user,
      quantidade,
      tipoOperacao,
      moedaCripto,
      podeBuscarCotacao,
      userBalance,
    ],
  );

  // Função para lidar com mudanças que exigem nova cotação
  const handleTipoOperacaoChange = (newValue) => {
    if (newValue && newValue !== tipoOperacao) {
      setTipoOperacao(newValue);
      // Limpa a cotação atual enquanto busca nova
      setQuote(null);
      setAwaitingNewQuote(true);
      setSuccessInfo(null); // Reseta estado de sucesso

      // Busca cotação automaticamente
      if (podeBuscarCotacao()) {
        buscarCotacao();
      }
    }
  };

  const handleMoedaCriptoChange = (newValue) => {
    if (newValue !== moedaCripto) {
      setMoedaCripto(newValue);
      // Limpa a cotação atual enquanto busca nova
      setQuote(null);
      setAwaitingNewQuote(true);
      setSuccessInfo(null); // Reseta estado de sucesso

      // Busca cotação automaticamente
      if (podeBuscarCotacao()) {
        buscarCotacao();
      }
    }
  };

  useEffect(() => {
    if (!quote?.expiration) return;

    if (expirationTimeoutRef.current) {
      clearTimeout(expirationTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    const atualizarContador = () => {
      const segundos = calcularTempoRestante(quote.expiration);
      setTimeLeft(formatarTempo(segundos));
    };

    atualizarContador();

    countdownIntervalRef.current = setInterval(atualizarContador, 1000);

    const segundosParaExpirar = calcularTempoRestante(quote.expiration);
    expirationTimeoutRef.current = setTimeout(() => {
      if (podeBuscarCotacao()) {
        buscarCotacao();
      }
    }, segundosParaExpirar * 1000);

    return () => {
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [quote, podeBuscarCotacao, buscarCotacao]);

  // Reseta o estado de sucesso quando a cotação muda
  useEffect(() => {
    if (quote && successInfo) {
      // Limpa o estado de sucesso após 3 segundos
      const timer = setTimeout(() => {
        setSuccessInfo(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [quote, successInfo]);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      if (user?.id && mounted) {
        const hasAccount = await verificarContaCripto();

        if (hasAccount === true && mounted) {
          await fetchUserBalance();
          await fetchMoedasDisponiveis(); // Buscar moedas da API
          await buscarCotacao(true);
        } else if (mounted) {
          setLoading(false);
        }
      } else if (mounted) {
        setLoading(false);
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (redirectTo) {
      const timer = setTimeout(() => {
        navigate(redirectTo);
        setRedirectTo(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [redirectTo, navigate]);

  // Cleanup dos timeouts
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const handleContinuar = async () => {
    if (hasCryptoAccount === false) {
      setShowCreateAccountDialog(true);
      return;
    }

    if (!quote || !quantidade || parseFloat(quantidade) <= 0) {
      showAlertError("Por favor, informe uma quantidade válida para continuar");
      return;
    }

    const segundosRestantes = calcularTempoRestante(quote.expiration);
    if (segundosRestantes <= 0) {
      showAlertError("Cotação expirada. Aguarde a nova cotação.");
      return;
    }

    setLoadingExecute(true);

    try {
      const moedaOrigem = getMoedaOrigem();
      const moedaDestino = getMoedaDestino();

      const valorTotal =
        quantidade && quote.price
          ? parseFloat(quantidade) * parseFloat(quote.price)
          : quote.quote_amount;

      // Encontrar o ID da moeda baseado nas moedas carregadas da API
      const moedaSelecionada = moedasCripto.find(
        (m) => m.codigo === moedaCripto,
      );
      const idMoedaCripto =
        moedaSelecionada?.id || (moedaCripto === "USDT" ? 2 : 3);

      const idMoedaDebito =
        tipoOperacao === "BUY" ? moedaIds["BRL"] : idMoedaCripto;
      const idMoedaCredito =
        tipoOperacao === "BUY" ? idMoedaCripto : moedaIds["BRL"];

      const transactionRequest = createExecuteTransactionRequest(
        quote.quote_id,
        quote.fee || 0,
        `EXT-${user.id}-${Date.now()}`,
        idMoedaCredito,
        idMoedaDebito,
        tipoOperacao !== "BUY",
      );

      const executeResult = await executeQuote(user.id, transactionRequest);

      if (executeResult.errorMessage && !executeResult.success) {
        throw new Error(executeResult.errorMessage);
      }

      if (executeResult.data && executeResult.data.data === true) {
        showAlertSuccess("Operação realizada com sucesso!");
        atualizarSaldo();

        setSuccessInfo({
          message: "Operação realizada com sucesso!",
          timestamp: new Date().toISOString(),
          orderId: executeResult.orderId || `ORD-${Date.now()}`,
        });

        // Limpa o formulário após sucesso
        setQuantidade("");
        setPercentage(0);

        // Busca nova cotação após a operação
        setTimeout(() => {
          if (podeBuscarCotacao()) {
            buscarCotacao();
          }
        }, 1000);
      } else if (executeResult.success === false) {
        let errorMessage =
          executeResult.message || "Erro ao executar transação";

        if (executeResult.messageCod) {
          const errorMessagesMap = {
            QUOTE_EXPIRED:
              "A cotação expirou. Por favor, atualize a cotação e tente novamente.",
            QUOTE_EXPIRED_OR_NOT_FOUND:
              "Cotação expirada ou não encontrada. Atualize a cotação.",
            QUOTE_NOT_FOUND: "Cotação não encontrada. Atualize a cotação.",
            INSUFFICIENT_FUNDS:
              "Fundos insuficientes para realizar a operação.",
            INVALID_QUOTE: "Cotação inválida. Atualize a cotação.",
            MARKET_CLOSED: "O mercado está fechado no momento.",
            NETWORK_ERROR: "Erro de conexão. Verifique sua internet.",
            API_ERROR: "Erro na comunicação com a exchange.",
            TIMEOUT: "Tempo esgotado. Tente novamente.",
          };

          errorMessage =
            errorMessagesMap[executeResult.messageCod] || errorMessage;

          if (executeResult.messageCod.includes("QUOTE_EXPIRED")) {
            errorMessage =
              "Cotação expirada. Por favor, atualize a cotação e tente novamente.";
          }
        }

        throw new Error(errorMessage);
      } else {
        throw new Error("Resposta inesperada do servidor.");
      }
    } catch (error) {
      console.error("Erro no processamento:", error);
      showAlertError(`Erro na transação: ${error.message}`);

      const reloadErrors = [
        "expirada",
        "expirou",
        "QUOTE_EXPIRED",
        "QUOTE_EXPIRED_OR_NOT_FOUND",
      ];

      const shouldReload = reloadErrors.some((err) =>
        error.message.toUpperCase().includes(err.toUpperCase()),
      );

      if (shouldReload) {
        setQuote(null);
        if (podeBuscarCotacao()) {
          setTimeout(() => {
            buscarCotacao();
          }, 1500);
        }
      }
    } finally {
      setLoadingExecute(false);
    }
  };

  const calcularValorEstimado = () => {
    if (!quote || !quote.price) return 0;
    if (!quantidade || parseFloat(quantidade) <= 0) return 0;

    const qty = parseFloat(quantidade);
    const price = parseFloat(quote.price);
    return qty * price;
  };

  if (loading || checkingAccount) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: binanceLightTheme.primary }} />
        <Typography
          variant="body2"
          sx={{ color: binanceLightTheme.textSecondary }}
        >
          {checkingAccount ? "Verificando sua conta..." : "Carregando..."}
        </Typography>
      </Box>
    );
  }

  if (hasCryptoAccount === false) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: binanceLightTheme.bgPrimary,
            border: `1px solid ${binanceLightTheme.border}`,
            borderRadius: 2,
            textAlign: "center",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <AccountBalanceWalletIcon
              sx={{ fontSize: 48, color: binanceLightTheme.primary, mb: 1 }}
            />
            <Typography
              variant="h6"
              sx={{
                color: binanceLightTheme.textPrimary,
                fontWeight: 600,
                mb: 1,
              }}
            >
              Conta de Criptomoedas Necessária
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: binanceLightTheme.textSecondary, mb: 2 }}
            >
              Para operar com criptomoedas, você precisa criar uma conta
              específica.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={criarContaCripto}
            disabled={creatingAccount}
            startIcon={
              creatingAccount ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <AddCircleOutlineIcon />
              )
            }
            sx={{
              bgcolor: binanceLightTheme.primary,
              color: "#000",
              fontWeight: 600,
              borderRadius: 1.5,
              px: 4,
              py: 1.5,
              "&:hover": { bgcolor: "#d4a506" },
              boxShadow: "0 4px 12px rgba(240, 185, 11, 0.3)",
            }}
          >
            {creatingAccount ? "Criando Conta..." : "Criar Conta Cripto"}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 1400, mx: "auto" }}>
      <Fade in timeout={500}>
        <Box>
          {/* Header Binance Style Light */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              sx={{
                color: binanceLightTheme.textPrimary,
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              Comprar/Vender Cripto
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: binanceLightTheme.textSecondary }}
            >
              Transações rápidas e seguras com taxa competitiva
            </Typography>
          </Box>

          {/* Componente de Saldo */}
          <SaldoCripto
            refreshTrigger={refreshSaldo}
            showDetailed={true}
            hidden={!showSaldo}
            onToggleVisibility={() => setShowSaldo(!showSaldo)}
            isVisible={showSaldo}
            moedasCripto={moedasCripto} // Passa as moedas da API
          />

          {/* Área Principal - Layout Binance Light */}
          <Grid container spacing={2}>
            {/* Coluna Esquerda - Trading Panel */}
            <Grid item xs={12} lg={8}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: binanceLightTheme.bgPrimary,
                  border: `1px solid ${binanceLightTheme.border}`,
                  borderRadius: 2,
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                }}
              >
                {/* Tabs de Compra/Venda */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: binanceLightTheme.textSecondary,
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    Tipo de Operação
                  </Typography>
                  <ToggleButtonGroup
                    value={tipoOperacao}
                    exclusive
                    onChange={(e, value) => handleTipoOperacaoChange(value)}
                    sx={{
                      width: "100%",
                      "& .MuiToggleButton-root": {
                        flex: 1,
                        py: 1.5,
                        border: `1px solid ${binanceLightTheme.border}`,
                        color: binanceLightTheme.textSecondary,
                        "&.Mui-selected": {
                          bgcolor:
                            tipoOperacao === "BUY"
                              ? binanceLightTheme.successLight
                              : binanceLightTheme.sellLight,
                          color:
                            tipoOperacao === "BUY"
                              ? binanceLightTheme.success
                              : binanceLightTheme.sellColor,
                          borderColor:
                            tipoOperacao === "BUY"
                              ? binanceLightTheme.success
                              : binanceLightTheme.sellColor,
                          fontWeight: 600,
                        },
                        "&:hover": {
                          bgcolor: binanceLightTheme.hover,
                        },
                      },
                    }}
                  >
                    <ToggleButton value="BUY">
                      <TrendingUpIcon sx={{ mr: 1, fontSize: "1rem" }} />
                      Comprar
                    </ToggleButton>
                    <ToggleButton value="SELL">
                      <TrendingDownIcon sx={{ mr: 1, fontSize: "1rem" }} />
                      Vender
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Seleção de Moeda */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: binanceLightTheme.textSecondary,
                      mb: 1,
                      fontWeight: 500,
                    }}
                  >
                    Selecionar Moeda
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={moedaCripto}
                    onChange={(e) => handleMoedaCriptoChange(e.target.value)}
                    size="medium"
                    disabled={loadingMoedas || moedasCripto.length === 0}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: binanceLightTheme.bgPrimary,
                        borderColor: binanceLightTheme.border,
                        color: binanceLightTheme.textPrimary,
                        "&:hover fieldset": {
                          borderColor: binanceLightTheme.primary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: binanceLightTheme.primary,
                          borderWidth: 2,
                        },
                      },
                      "& .MuiSelect-icon": {
                        color: binanceLightTheme.textSecondary,
                      },
                    }}
                  >
                    {loadingMoedas ? (
                      <MenuItem disabled>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 0.5,
                          }}
                        >
                          <CircularProgress
                            size={20}
                            sx={{ color: binanceLightTheme.primary }}
                          />
                          <Typography variant="body2">
                            Carregando moedas...
                          </Typography>
                        </Box>
                      </MenuItem>
                    ) : moedasCripto.length === 0 ? (
                      <MenuItem disabled>
                        <Typography
                          variant="body2"
                          sx={{ color: binanceLightTheme.textSecondary }}
                        >
                          Nenhuma moeda disponível
                        </Typography>
                      </MenuItem>
                    ) : (
                      moedasCripto.map((moeda) => (
                        <MenuItem
                          key={moeda.codigo}
                          value={moeda.codigo}
                          disabled={!moeda.ativo}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              py: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                bgcolor: binanceLightTheme.bgSecondary,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: binanceLightTheme.primary,
                              }}
                            >
                              {moeda.icon}
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: moeda.ativo
                                    ? binanceLightTheme.textPrimary
                                    : binanceLightTheme.textSecondary,
                                  fontWeight: 500,
                                }}
                              >
                                {moeda.codigo}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: binanceLightTheme.textSecondary }}
                              >
                                {moeda.nome}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Box>

                {/* Quantidade */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: binanceLightTheme.textSecondary,
                        fontWeight: 500,
                      }}
                    >
                      Quantidade ({moedaCripto})
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {porcentagens.map((pct) => (
                        <Button
                          key={pct}
                          size="small"
                          variant="outlined"
                          onClick={() => handlePercentageClick(pct)}
                          sx={{
                            minWidth: 40,
                            height: 24,
                            fontSize: "0.75rem",
                            borderColor: binanceLightTheme.border,
                            color:
                              percentage === pct
                                ? binanceLightTheme.primary
                                : binanceLightTheme.textSecondary,
                            bgcolor:
                              percentage === pct
                                ? `${binanceLightTheme.primary}15`
                                : "transparent",
                            "&:hover": {
                              borderColor: binanceLightTheme.primary,
                              bgcolor: `${binanceLightTheme.primary}10`,
                            },
                          }}
                        >
                          {pct}%
                        </Button>
                      ))}
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    type="number"
                    value={quantidade}
                    onChange={handleQuantidadeChange}
                    placeholder="0.00"
                    size="medium"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: binanceLightTheme.bgPrimary,
                        borderColor: binanceLightTheme.border,
                        color: binanceLightTheme.textPrimary,
                        "&:hover fieldset": {
                          borderColor: binanceLightTheme.primary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: binanceLightTheme.primary,
                          borderWidth: 2,
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Chip
                            label={moedaCripto}
                            size="small"
                            sx={{
                              bgcolor: binanceLightTheme.primaryLight,
                              color: binanceLightTheme.primary,
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: binanceLightTheme.textSecondary,
                      mt: 1,
                      display: "block",
                    }}
                  >
                    Saldo disponível:{" "}
                    {tipoOperacao === "BUY"
                      ? formatNumber(userBalance.BRL, 2, "BRL")
                      : `${formatNumber(userBalance[moedaCripto], 8)} ${moedaCripto}`}
                  </Typography>
                </Box>

                {/* Informações de Cotação */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: binanceLightTheme.bgSecondary,
                    borderRadius: 2,
                    border: `1px solid ${binanceLightTheme.border}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: binanceLightTheme.textSecondary,
                        fontWeight: 500,
                      }}
                    >
                      Cotação Atual
                    </Typography>
                    {quote && !awaitingNewQuote ? (
                      <Chip
                        label={timeLeft || "00:00"}
                        size="small"
                        sx={{
                          bgcolor:
                            parseInt(timeLeft?.split(":")[0] || 0) < 1
                              ? `${binanceLightTheme.warning}15`
                              : `${binanceLightTheme.success}15`,
                          color:
                            parseInt(timeLeft?.split(":")[0] || 0) < 1
                              ? binanceLightTheme.warning
                              : binanceLightTheme.success,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    ) : (
                      <Skeleton
                        width={40}
                        height={24}
                        sx={{
                          bgcolor: binanceLightTheme.border,
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </Box>

                  {awaitingNewQuote || loadingQuote ? (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <CircularProgress
                        size={20}
                        sx={{ color: binanceLightTheme.primary }}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: binanceLightTheme.textSecondary }}
                        >
                          Atualizando cotação...
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: binanceLightTheme.textSecondary }}
                        >
                          Aguarde alguns segundos
                        </Typography>
                      </Box>
                    </Box>
                  ) : quote ? (
                    <Typography
                      variant="h5"
                      sx={{
                        color: binanceLightTheme.textPrimary,
                        fontWeight: 700,
                      }}
                    >
                      1 {moedaCripto} = {formatNumber(quote.price, 4, "BRL")}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: binanceLightTheme.textSecondary,
                        fontStyle: "italic",
                      }}
                    >
                      Informe os dados para ver a cotação
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Coluna Direita - Order Summary */}
            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: binanceLightTheme.bgPrimary,
                  border: `1px solid ${binanceLightTheme.border}`,
                  borderRadius: 2,
                  height: "100%",
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: binanceLightTheme.textPrimary,
                    fontWeight: 600,
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ReceiptIcon fontSize="small" />
                  Resumo da Ordem
                </Typography>

                {awaitingNewQuote || loadingQuote ? (
                  // Estado de carregamento enquanto busca nova cotação
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Skeleton
                        width="60%"
                        height={20}
                        sx={{
                          bgcolor: binanceLightTheme.border,
                          mb: 1,
                          borderRadius: 1,
                        }}
                      />
                      <Skeleton
                        width="100%"
                        height={60}
                        sx={{
                          bgcolor: binanceLightTheme.border,
                          borderRadius: 1.5,
                        }}
                      />
                    </Box>
                    <Box>
                      <Skeleton
                        width="40%"
                        height={20}
                        sx={{
                          bgcolor: binanceLightTheme.border,
                          mb: 1,
                          borderRadius: 1,
                        }}
                      />
                      <Skeleton
                        width="100%"
                        height={40}
                        sx={{
                          bgcolor: binanceLightTheme.border,
                          borderRadius: 1.5,
                        }}
                      />
                    </Box>
                    <Box>
                      <Skeleton
                        width="50%"
                        height={20}
                        sx={{
                          bgcolor: binanceLightTheme.border,
                          mb: 1,
                          borderRadius: 1,
                        }}
                      />
                      <Skeleton
                        width="100%"
                        height={80}
                        sx={{
                          bgcolor: binanceLightTheme.border,
                          borderRadius: 1.5,
                        }}
                      />
                    </Box>
                    <Skeleton
                      width="100%"
                      height={48}
                      sx={{
                        bgcolor: binanceLightTheme.border,
                        borderRadius: 1.5,
                        mt: 2,
                      }}
                    />
                  </Box>
                ) : quote ? (
                  <Stack spacing={2}>
                    {/* Order Type */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: binanceLightTheme.textSecondary,
                          mb: 0.5,
                          display: "block",
                          fontWeight: 500,
                        }}
                      >
                        Tipo de Ordem
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: binanceLightTheme.bgSecondary,
                          borderRadius: 1.5,
                          borderLeft: `3px solid ${tipoOperacao === "BUY" ? binanceLightTheme.success : binanceLightTheme.sellColor}`,
                          border: `1px solid ${binanceLightTheme.border}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: binanceLightTheme.textPrimary,
                              fontWeight: 600,
                            }}
                          >
                            {tipoOperacao === "BUY" ? "COMPRA" : "VENDA"}
                          </Typography>
                          <Chip
                            label={`${getMoedaOrigem()} → ${getMoedaDestino()}`}
                            size="small"
                            sx={{
                              bgcolor:
                                tipoOperacao === "BUY"
                                  ? binanceLightTheme.successLight
                                  : binanceLightTheme.sellLight,
                              color:
                                tipoOperacao === "BUY"
                                  ? binanceLightTheme.success
                                  : binanceLightTheme.sellColor,
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Quantity */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: binanceLightTheme.textSecondary,
                          mb: 0.5,
                          display: "block",
                          fontWeight: 500,
                        }}
                      >
                        Quantidade
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: binanceLightTheme.bgSecondary,
                          borderRadius: 1.5,
                          border: `1px solid ${binanceLightTheme.border}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              color: binanceLightTheme.textPrimary,
                              fontWeight: 700,
                            }}
                          >
                            {formatNumber(quantidade, 6)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: binanceLightTheme.primary,
                              fontWeight: 600,
                            }}
                          >
                            {moedaCripto}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: binanceLightTheme.textSecondary,
                            mt: 0.5,
                            display: "block",
                          }}
                        >
                          × {formatNumber(quote.price, 4)} por unidade
                        </Typography>
                      </Box>
                    </Box>

                    {/* Total Amount */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: binanceLightTheme.textSecondary,
                          mb: 0.5,
                          display: "block",
                          fontWeight: 500,
                        }}
                      >
                        Valor Total
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor:
                            tipoOperacao === "BUY"
                              ? binanceLightTheme.successLight
                              : binanceLightTheme.sellLight,
                          borderRadius: 1.5,
                          border: `1px solid ${tipoOperacao === "BUY" ? binanceLightTheme.success : binanceLightTheme.sellColor}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              color: binanceLightTheme.textPrimary,
                              fontWeight: 800,
                            }}
                          >
                            {formatNumber(calcularValorEstimado(), 2, "BRL")}
                          </Typography>
                          <Chip
                            label={
                              tipoOperacao === "BUY" ? "A PAGAR" : "A RECEBER"
                            }
                            size="small"
                            sx={{
                              bgcolor:
                                tipoOperacao === "BUY"
                                  ? binanceLightTheme.success
                                  : binanceLightTheme.sellColor,
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: binanceLightTheme.textSecondary,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                          Taxas incluídas • Válido por {timeLeft}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Botão de confirmação */}
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleContinuar}
                      disabled={
                        loadingExecute ||
                        loadingPedido ||
                        !quantidade ||
                        parseFloat(quantidade) <= 0 ||
                        awaitingNewQuote ||
                        successInfo // Desabilita apenas se successInfo existir
                      }
                      sx={{
                        mt: 2,
                        py: 1.5,
                        bgcolor:
                          tipoOperacao === "BUY"
                            ? binanceLightTheme.success
                            : binanceLightTheme.sellColor,
                        color: tipoOperacao === "BUY" ? "#fff" : "#000",
                        fontWeight: 700,
                        fontSize: "1rem",
                        borderRadius: 1.5,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor:
                            tipoOperacao === "BUY" ? "#0db972" : "#d4a506",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        },
                        "&.Mui-disabled": {
                          bgcolor: binanceLightTheme.border,
                          color: binanceLightTheme.textSecondary,
                        },
                      }}
                    >
                      {loadingExecute ? (
                        <>
                          <CircularProgress
                            size={20}
                            sx={{
                              color: tipoOperacao === "BUY" ? "#fff" : "#000",
                              mr: 1,
                            }}
                          />
                          Processando...
                        </>
                      ) : successInfo ? (
                        <>
                          <CheckCircleIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                          Concluído
                        </>
                      ) : tipoOperacao === "BUY" ? (
                        `Comprar ${moedaCripto}`
                      ) : (
                        `Vender ${moedaCripto}`
                      )}
                    </Button>

                    {/* Mensagem de sucesso temporária */}
                    {successInfo && (
                      <Alert
                        severity="success"
                        sx={{ mt: 2 }}
                        onClose={() => setSuccessInfo(null)}
                      >
                        Operação realizada com sucesso! Você já pode fazer uma
                        nova operação.
                      </Alert>
                    )}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 300,
                      color: binanceLightTheme.textSecondary,
                    }}
                  >
                    <CurrencyExchangeIcon
                      sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                    />
                    <Typography variant="body2">
                      Configure a operação para ver o resumo
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Componente de Transações */}
          <Box sx={{ mt: 3 }}>
            <TransacoesCripto />
          </Box>
        </Box>
      </Fade>

      {/* Dialog de confirmação para criar conta */}
      <Dialog
        open={showCreateAccountDialog}
        onClose={() => setShowCreateAccountDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: binanceLightTheme.textPrimary,
            borderBottom: `1px solid ${binanceLightTheme.border}`,
            fontWeight: 600,
          }}
        >
          Criar Conta de Criptomoedas
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <AccountBalanceWalletIcon
              sx={{ fontSize: 48, color: binanceLightTheme.primary, mb: 2 }}
            />
            <DialogContentText sx={{ color: binanceLightTheme.textSecondary }}>
              Para continuar com a operação, você precisa criar uma conta de
              criptomoedas. Deseja criar a conta agora?
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ p: 2, borderTop: `1px solid ${binanceLightTheme.border}` }}
        >
          <Button
            onClick={() => setShowCreateAccountDialog(false)}
            sx={{ color: binanceLightTheme.textSecondary, fontWeight: 500 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={criarContaCripto}
            variant="contained"
            disabled={creatingAccount}
            sx={{
              bgcolor: binanceLightTheme.primary,
              color: "#000",
              fontWeight: 600,
              borderRadius: 1,
              px: 3,
              "&:hover": {
                bgcolor: "#d4a506",
              },
            }}
          >
            {creatingAccount ? "Criando..." : "Criar Conta"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cripto;
